export function formatTimeAgo(dateInput?: string | Date | number | null): string {
  if (!dateInput) return "";
  const d = typeof dateInput === "object" ? dateInput : new Date(dateInput);
  if (isNaN(d.getTime())) return "";

  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - d.getTime()) / 1000);

  if (diffInSeconds < 0) return "just now";
  if (diffInSeconds < 60) return "just now";

  const minutes = Math.floor(diffInSeconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(diffInSeconds / 3600);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(diffInSeconds / 86400);
  if (days < 30) return `${days}d ago`;

  const months = Math.floor(diffInSeconds / 2592000);
  if (months < 12) return `${months}mo ago`;

  const years = Math.max(1, Math.floor(diffInSeconds / 31536000));
  return `${years}y ago`;
}

export function formatFullTimestamp(dateInput?: string | Date | number | null): string {
  if (!dateInput) return "";
  const d = typeof dateInput === "object" ? dateInput : new Date(dateInput);
  if (isNaN(d.getTime())) return "";

  return d.toLocaleString(undefined, {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export function getKnowledgeItemCreator(item?: {
  source_type?: string | null;
  metadata?: Record<string, unknown> | null;
} | null): string | null {
  if (!item) return null;
  const meta = (item.metadata || {}) as Record<string, any>;
  if (typeof meta.author === "string" && meta.author.trim()) return meta.author.trim();
  if (typeof meta.created_by_name === "string" && meta.created_by_name.trim()) return meta.created_by_name.trim();
  if (typeof meta.created_by === "string" && meta.created_by.trim()) return meta.created_by.trim();
  if (typeof meta.seeded_by === "string" && meta.seeded_by.trim()) return meta.seeded_by.trim();
  if (item.source_type && item.source_type !== "manual") return item.source_type;
  if (item.source_type === "manual") return "Manual entry";
  return null;
}

interface TimeAgoProps {
  date?: string | Date | number | null;
  prefix?: string;
  suffix?: string;
  className?: string;
  titlePrefix?: string;
  title?: string;
}

export function TimeAgo({
  date,
  prefix = "",
  suffix = "",
  className = "",
  titlePrefix = "Created: ",
  title,
}: TimeAgoProps) {
  if (!date) return null;

  const timeAgo = formatTimeAgo(date);
  const fullTimestamp = formatFullTimestamp(date);
  const iso = typeof date === "object" ? date.toISOString() : new Date(date).toISOString();

  return (
    <time
      dateTime={iso}
      title={title || `${titlePrefix}${fullTimestamp}`}
      className={`cursor-help decoration-dotted underline-offset-2 hover:underline ${className}`}
    >
      {prefix}
      {timeAgo}
      {suffix}
    </time>
  );
}
